/**
 * Local draft model for the participation checklist.
 *
 * The checklist is edited offline-of-the-server and saved in one batch, so the
 * page needs a diff: which rows actually changed since the last load. Split out
 * of `participation-section.tsx` so the component file stays presentational.
 */
import type { ParticipationEntry } from '../../lib/api-types';

export interface DraftEntry {
  participated: boolean;
  notes: string;
}

export type DraftMap = Readonly<Record<string, DraftEntry>>;

export function toParticipationDraft(entries: readonly ParticipationEntry[]): DraftMap {
  return Object.fromEntries(
    entries.map((entry) => [
      entry.player_id,
      { participated: entry.participated, notes: entry.notes ?? '' },
    ])
  );
}

/**
 * An unrecorded player counts as changed the moment anyone answers for them,
 * even when the answer matches the `false` the API projects for "no row yet".
 */
export function isParticipationChanged(
  entry: ParticipationEntry,
  draft: DraftEntry | undefined
): boolean {
  if (draft === undefined) return false;
  if (!entry.is_recorded) return draft.participated || draft.notes.trim() !== '';
  return draft.participated !== entry.participated || draft.notes.trim() !== (entry.notes ?? '');
}

/** One line per player: jersey, name, cohort and position. Program data only. */
export function describePlayer(entry: ParticipationEntry): string {
  const jersey = entry.jersey_number === null ? '—' : `#${entry.jersey_number}`;
  const position = entry.position === null ? '' : ` · ${entry.position}`;
  return `${jersey} ${entry.full_name} · ${entry.cohort}${position}`;
}
